import { BaseService, Environment } from '@lib/classes';
import { map, Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Params } from '@angular/router';
import { PagedData, Pagination } from '../api/paged-data.class';

export class BaseApiCrud<T, Request = any> extends BaseService<T> {
  constructor(
    public override environment: Environment,
    public http: HttpClient,
  ) {
    super(environment);
  }

  get(filters?: Record<string, any>, headers?: any): Observable<T> {
    filters = {
      ...filters,
      limit: 100,
    };
    let params = this.getFilterParams(filters ?? {});
    return this.http.get<T>(this.url, { params, headers });
  }

  getAll(filters?: Params): Observable<T[]> {
    let params = this.getFilterParams(filters ?? {});
    return this.http.get<T[]>(this.url, { params });
  }

  getPaged(
    pagination: Pagination,
    filters?: Record<string, any>,
  ): Observable<PagedData<T>> {
    let params = this.getFilterParams({
      ...(filters && filters),
      ...pagination,
    });

    return this.http.get<PagedData<T>>(this.url, { params }).pipe(
      map((data: PagedData<T>) => {
        return this.deserializePagedData(data);
      }),
    );
  }

  getOne(id: string | number): Observable<T> {
    return this.http.get<T>(`${this.url}/${id}`).pipe(
      map((resource: T) => {
        return super.deserialize(resource) as T;
      }),
    );
  }

  create(body: Request): Observable<T> {
    return this.http
      .post<T>(this.url, body)
      .pipe(map((resource: T) => resource));
  }

  upload(
    id: string | number,
    files: File[],
    isMultiple: boolean = false,
  ): Observable<T | T[]> {
    const formData = new FormData();
    for (let file of files) {
      formData.append(isMultiple ? 'files' : 'file', file, file.name);
    }
    return this.http
      .post<T | T[]>(this.url, formData)
      .pipe(map((resource: T | T[]) => resource));
  }

  save(model: T): Observable<T> {
    const id = (model as any)['id'];
    if ((model as any)['id']) {
      return this.update(id, model);
    } else {
      return this.create(model as any);
    }
  }
  update(id: string | number, body: any): Observable<T> {
    return this.http
      .put<T>(`${this.url}/${id}`, body)
      .pipe(map((resource: T) => resource));
  }

  delete(id: string | number) {
    return this.http.delete(`${this.url}/${id}`);
  }
}
